import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  DealerInvitationView,
  DealerListItem,
  DealerReviewAction,
  DealerRole,
  DealerStatus,
  DealerTeamResponse,
  DealerView,
} from '@tex/finance-shared'
import { apiGet, apiSend } from './api'

const base = '/api/v1/dealers'

export const dealerKeys = {
  list: (status?: DealerStatus) => ['dealers', 'list', status ?? 'all'] as const,
  detail: (id: string) => ['dealers', id] as const,
  team: (id: string) => ['dealers', id, 'team'] as const,
}

export function useDealers(status?: DealerStatus) {
  const qs = status ? `?status=${status}` : ''
  return useQuery({
    queryKey: dealerKeys.list(status),
    queryFn: () => apiGet<{ dealers: DealerListItem[] }>(`${base}${qs}`),
  })
}

export function useDealer(id: string) {
  return useQuery({ queryKey: dealerKeys.detail(id), queryFn: () => apiGet<DealerView>(`${base}/${id}`) })
}

export function useDealerTeam(id: string) {
  return useQuery({ queryKey: dealerKeys.team(id), queryFn: () => apiGet<DealerTeamResponse>(`${base}/${id}/team`) })
}

/** `null` clears an optional field on the server; omit to leave it unchanged. */
export interface DealerProfileInput {
  legal_name: string
  trading_name?: string | null
  website?: string | null
  equipment_categories?: string[]
}

export interface DealerBrandingInput {
  display_name?: string
  logo_key?: string | null
  accent_color?: string | null
}

export interface DealerReviewInput {
  action: DealerReviewAction
  verification_method?: string
  reviewer_notes?: string
  reason?: string
}

function useDealerMutation<TVars, TResult>(
  dealerId: string,
  fn: (vars: TVars) => Promise<TResult>,
  extraKeys: readonly (readonly string[])[] = [],
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await Promise.all(
        [dealerKeys.detail(dealerId), ['dealers', 'list'] as const, ...extraKeys].map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      )
    },
  })
}

export function useCreateDealer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: DealerProfileInput & { reviewer_notes?: string }) => apiSend<DealerView>('POST', base, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dealers', 'list'] }),
  })
}

export function useUpdateDealerProfile(dealerId: string) {
  return useDealerMutation(dealerId, (patch: Partial<DealerProfileInput>) =>
    apiSend<DealerView>('PATCH', `${base}/${dealerId}`, patch),
  )
}

export function useUpdateDealerBranding(dealerId: string) {
  return useDealerMutation(dealerId, (patch: DealerBrandingInput) =>
    apiSend<DealerView>('PATCH', `${base}/${dealerId}/branding`, patch),
  )
}

export function useReviewDealer(dealerId: string) {
  return useDealerMutation(dealerId, (input: DealerReviewInput) =>
    apiSend<DealerView>('POST', `${base}/${dealerId}/review`, input),
  )
}

export function useInviteMember(dealerId: string) {
  return useDealerMutation(
    dealerId,
    (input: { email: string; role: DealerRole }) =>
      apiSend<DealerInvitationView>('POST', `${base}/${dealerId}/invitations`, input),
    [dealerKeys.team(dealerId)],
  )
}

export function useCancelInvitation(dealerId: string) {
  return useDealerMutation(
    dealerId,
    (invitationId: string) => apiSend<void>('DELETE', `${base}/${dealerId}/invitations/${invitationId}`),
    [dealerKeys.team(dealerId)],
  )
}
